import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Briefcase, 
  Users, 
  Target, 
  BookOpen, 
  Shield, 
  CheckCircle,
  Mail,
  ArrowRight,
  Award,
  TrendingUp,
  Scale,
  Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

import usePageSeo from '../hooks/usePageSeo';
const Careers = () => {
  usePageSeo({
    title: 'Careers at WPAA | Join Our UAE Audit & Tax Team',
    description: 'Build your career at White Paper Accounts Auditing. Explore audit, tax, accounting and advisory job opportunities in UAE.',
    path: '/careers'
  });

  const expectations = [
    { text: 'Strong understanding of accounting, audit, and compliance principles', icon: BookOpen },
    { text: 'Commitment to integrity, confidentiality, and ethical standards', icon: Shield },
    { text: 'Ability to follow company policies and professional guidelines', icon: CheckCircle },
    { text: 'Willingness to learn and adapt in a dynamic regulatory environment', icon: TrendingUp },
    { text: 'Good communication and teamwork skills', icon: Users },
    { text: 'Attention to detail and responsibility towards deadlines', icon: Target }
  ];

  const values = [
    { 
      title: 'Integrity & Transparency', 
      description: 'We maintain the highest ethical standards in all our engagements',
      icon: Shield,
      color: 'teal'
    },
    { 
      title: 'Regulatory Compliance', 
      description: 'Full adherence to UAE laws and regulatory requirements',
      icon: Scale,
      color: 'blue'
    },
    { 
      title: 'Professional Discipline', 
      description: 'Accountability and responsibility in every task we undertake',
      icon: Award,
      color: 'purple'
    },
    { 
      title: 'Continuous Learning', 
      description: 'Commitment to ongoing improvement and knowledge development',
      icon: BookOpen,
      color: 'green'
    }
  ];

  const benefits = [
    'Exposure to audit, VAT, corporate tax, and AML engagements',
    'Professional working environment',
    'Opportunities for learning and career growth',
    'Competitive compensation aligned with market standards',
    'Compliance with UAE labour laws and regulatory requirements'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-teal-900 via-teal-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-teal-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-teal-300 rounded-full filter blur-3xl"></div>
        </div>
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center space-x-2 mb-6">
              <Briefcase className="text-teal-400" size={32} />
              <span className="text-teal-400 font-semibold text-sm uppercase tracking-wider">Careers</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Join Our Team
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-3xl">
              Build your career with White Paper Accounts Auditing. We're looking for talented professionals who share our commitment to excellence, integrity, and continuous growth.
            </p>
            <a href="mailto:jobs@wpaauae.com">
              <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-6 text-lg">
                Apply Now
                <Mail className="ml-2" size={20} />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* About WPAA */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">About Us</span>
              <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">
                About White Paper Accounts Auditing
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                At White Paper Accounts Auditing (WPAA), we are committed to delivering high-quality audit, tax, and compliance services across the UAE.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                As a growing firm, we focus on building a team of professionals who value integrity, continuous learning, and professional excellence.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                We believe in creating an environment where individuals can develop strong expertise in audit, VAT, corporate tax, and AML compliance while contributing to meaningful client engagements.
              </p>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&h=600&fit=crop&q=75&fm=webp" 
                alt="Team collaboration" 
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-teal-600 text-white p-6 rounded-xl shadow-lg">
                <p className="text-3xl font-bold">18+</p>
                <p className="text-sm">Years of Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Expect */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Requirements</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">What We Expect from Candidates</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We are looking for individuals who demonstrate the following qualities and skills.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {expectations.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-teal-100 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="text-teal-600" size={24} />
                      </div>
                      <p className="text-gray-700 font-medium">{item.text}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Professional Values */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Our Culture</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Professional Values at WPAA</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our team operates on the following core principles.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              const colorMap = {
                teal: 'bg-teal-100 text-teal-600',
                blue: 'bg-blue-100 text-blue-600',
                purple: 'bg-purple-100 text-purple-600',
                green: 'bg-green-100 text-green-600'
              };
              return (
                <Card key={index} className="border-2 hover:border-teal-500 transition-all hover:shadow-lg text-center">
                  <CardContent className="p-8">
                    <div className={`${colorMap[value.color]} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4`}>
                      <Icon size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                    <p className="text-gray-600">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20 px-4 bg-gradient-to-br from-teal-900 via-teal-800 to-gray-900 text-white">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-teal-400 font-semibold text-sm uppercase tracking-wider">Benefits</span>
              <h2 className="text-4xl font-bold mt-4 mb-8">What We Offer</h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="bg-teal-500 rounded-full p-1 mt-1">
                      <CheckCircle size={16} className="text-white" />
                    </div>
                    <p className="text-lg text-gray-200">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <Heart className="text-teal-400 mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Grow With Us</h3>
              <p className="text-gray-300 mb-6">
                At WPAA, we invest in our people. Whether you're starting your career or looking to advance, we provide the support and opportunities you need to succeed.
              </p>
              <div className="flex items-center space-x-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-teal-400">200+</p>
                  <p className="text-sm text-gray-400">Clients Served</p>
                </div>
                <div className="w-px h-12 bg-white/20"></div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-teal-400">100%</p>
                  <p className="text-sm text-gray-400">Compliance Rate</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Apply */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto text-center max-w-3xl">
          <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Get Started</span>
          <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">How to Apply</h2>
          <p className="text-xl text-gray-600 mb-8">
            If you are interested in joining our team, please send your CV to:
          </p>
          
          <div className="bg-teal-50 border-2 border-teal-200 rounded-2xl p-8 mb-8">
            <Mail className="text-teal-600 mx-auto mb-4" size={48} />
            <a href="mailto:jobs@wpaauae.com" className="text-3xl font-bold text-teal-600 hover:text-teal-700 transition-colors">
              jobs@wpaauae.com
            </a>
            <p className="text-gray-600 mt-4">
              Please include your updated CV and a brief cover letter describing your experience and interest.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:jobs@wpaauae.com">
              <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-6 text-lg">
                Send Your CV
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2 border-teal-600 text-teal-600 hover:bg-teal-50 px-8 py-6 text-lg">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
